import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import { IconEdit } from '@tabler/icons-react'
import { api } from 'src/api/root'
import { ChangePasswordBody } from 'src/types/user'
import Typography from 'src/components/atoms/Typography'
import Loader from 'src/components/atoms/Loader'
import DashboardHeader from 'src/components/molecules/DashboardHeader'
import { formatDate } from 'src/enviroment/lib/formatters/format-date'
import EditProfileForm from 'src/components/organisms/EditProfileForm'
import { SecondaryButton } from 'src/components/atoms/buttons/SecondaryButton'
import { IUser } from 'src/types/user'
import ChangePasswordForm from 'src/components/organisms/ChangePasswordForm'
import LogoutDialog from 'src/components/molecules/LogoutDialog'
import AvatarUpload from 'src/components/molecules/AvatarUpload'
import { GradientBorderButton } from '../../components/atoms/buttons/GradientBorderButton'

interface ProfileFieldProps {
  label: string
  value: string
}

const ProfileField = ({ label, value }: ProfileFieldProps) => (
  <div className="flex-1">
    <Typography variant="sm-medium" className="mb-2 text-gray-100">
      {label}
    </Typography>
    <Typography variant="lg-medium" className="text-light-green-50">
      {value}
    </Typography>
  </div>
)

const MyProfilePage = () => {
  const { mutateAsync: mutateAsyncUser, isLoading: isUpdatingUser } = api.user.currentUser.update.useMutation()
  const { mutateAsync: changePassword, isLoading: isChangingPasswordLoading } = api.user.changePassword.useMutation()
  const { data: user, isLoading, error } = api.user.currentUser.useQuery()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false)

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="flex h-full items-center justify-center text-red-500">
        <Typography variant="lg">Error loading profile</Typography>
      </div>
    )
  }

  const handleProfileEdit = async (data: Pick<IUser, 'company' | 'firstName' | 'lastName'>) => {
    await mutateAsyncUser(data)
    setIsEditing(false)
  }

  const handlePasswordChange = async (data: ChangePasswordBody) => {
    await changePassword(data)
    setIsChangingPassword(false)
  }

  return (
    <>
      <Helmet>
        <title>RAMO - My Profile</title>
      </Helmet>

      <DashboardHeader title="Welcome to your Profile" subtitle="Make sure all your information is up to date" />

      <div className="flex-1 space-y-8 p-6">
        {/* Personal Information Section */}
        <div className="rounded-lg bg-gray-800 p-8">
          <div className="mb-8 flex justify-between">
            <Typography variant="xl-bold" className="text-light-green-50">
              Personal Information
            </Typography>
            <GradientBorderButton onClick={() => setIsEditing(!isEditing)} iconOnly>
              <IconEdit className="h-6 w-6" />
            </GradientBorderButton>
          </div>
          {user && <AvatarUpload user={user} />}

          {isEditing ? (
            <EditProfileForm
              initialData={{
                firstName: user.firstName,
                lastName: user.lastName,
                company: user.company,
              }}
              isLoading={isUpdatingUser}
              onSubmit={handleProfileEdit}
              onCancel={() => setIsEditing(false)}
            >
              <div className="mt-6 flex gap-4">
                <ProfileField label="Member Since" value={formatDate(user.createdAt)} />
                <ProfileField label="Last Updated" value={formatDate(user.updatedAt)} />
              </div>
            </EditProfileForm>
          ) : (
            <>
              <div className="flex flex-col gap-4 lg:flex-row">
                <div className="flex flex-1 gap-4">
                  <ProfileField label="First Name" value={user.firstName} />
                  <ProfileField label="Last Name" value={user.lastName} />
                </div>
                <ProfileField label="Company" value={user.company} />
              </div>

              <div className="mt-6 flex gap-4">
                <ProfileField label="Member Since" value={formatDate(user.createdAt)} />
                <ProfileField label="Last Updated" value={formatDate(user.updatedAt)} />
              </div>
            </>
          )}
        </div>

        {/* Password Section */}
        <div className="rounded-lg bg-gray-800 p-8">
          {isChangingPassword ? (
            <ChangePasswordForm
              isLoading={isChangingPasswordLoading}
              onSubmit={handlePasswordChange}
              onCancel={() => setIsChangingPassword(false)}
            />
          ) : (
            <>
              <div className="flex justify-between">
                <Typography variant="xl-bold" className="text-light-green-50">
                  Password
                </Typography>
                <GradientBorderButton onClick={() => setIsChangingPassword(true)}>Change Password</GradientBorderButton>
              </div>

              <div className="space-y-2">
                <Typography variant="sm-medium" className="text-gray-100">
                  Current Password
                </Typography>
                <div className="flex items-center space-x-2">
                  <Typography variant="base" className="text-light-green-50">
                    ••••••••••
                  </Typography>
                </div>
              </div>
            </>
          )}
        </div>
        <LogoutDialog />
      </div>
    </>
  )
}

export default MyProfilePage
